import { useMemo, type ReactElement, type ComponentType } from "react";
import { TooltipLineMarker } from "@atlas-int/viz/shared";
import { ProductClass, TradeDirection, TradeFlow } from "@atlas-int/data/types";
import { UIView } from "@atlas-int/data";
import { XAxisTooltipValue } from "../types";
import { CHART_MARGIN } from "../constants";
import type { TooltipDatum } from "@atlas-int/viz/shared";

export interface TooltipContentProps {
  datum: TooltipDatum;
  view: UIView;
  totalValue: number;
  tradeDirection: TradeDirection;
  productClass: ProductClass;
  fillColor: string;
}

interface TooltipElementsConfig {
  tooltipDisplay: {
    xAxisValue: XAxisTooltipValue | null;
    contentData: Record<string, unknown>;
    fillColor: string;
    yAxisMarkerExtent: { min: number; max: number } | null;
  } | null;
  chartContainerOffset: { left: number; top: number } | undefined;
  view: UIView;
  totalValue: number;
  tradeDirection: string;
  productClass: ProductClass;
  yAxis: string;
  tradeFlow: string;
  isLoading: boolean;
  overtimeElement: React.RefObject<HTMLElement>;
  /** Stable component reference — use memo() to avoid unnecessary rerenders. */
  TooltipContent?: ComponentType<TooltipContentProps>;
}

/**
 * Generates tooltip elements using derived state (useMemo)
 * Returns tooltip content and line marker as pure computations
 */
export const useTooltipElements = ({
  tooltipDisplay,
  chartContainerOffset,
  view,
  totalValue,
  tradeDirection,
  productClass,
  yAxis,
  tradeFlow,
  isLoading,
  overtimeElement,
  TooltipContent,
}: TooltipElementsConfig): {
  tooltipContentElement: ReactElement | null;
  tooltipLineMarkerElement: ReactElement | null;
} => {
  const tooltipContentElement = useMemo((): ReactElement | null => {
    if (!tooltipDisplay || !tooltipDisplay.contentData || isLoading) {
      return null;
    }

    if (!TooltipContent) return null;
    return (
      <TooltipContent
        datum={tooltipDisplay.contentData as unknown as TooltipDatum}
        view={view}
        totalValue={totalValue}
        tradeDirection={tradeDirection as TradeDirection}
        productClass={productClass}
        fillColor={tooltipDisplay.fillColor}
      />
    );
  }, [tooltipDisplay, view, totalValue, tradeDirection, productClass, isLoading, TooltipContent]);

  const tooltipLineMarkerElement = useMemo((): ReactElement | null => {
    if (
      !tooltipDisplay?.xAxisValue ||
      !tooltipDisplay.yAxisMarkerExtent ||
      !chartContainerOffset ||
      isLoading
    ) {
      return null;
    }

    const { year, x, xScale, yScale } = tooltipDisplay.xAxisValue;
    const yAxisMarkerExtent = tooltipDisplay.yAxisMarkerExtent;

    const chartMargin = {
      left: CHART_MARGIN.LEFT + CHART_MARGIN.Y_AXIS_SELECTOR_WIDTH - 60, // Adjust for base margin (same as useChartScales)
      right: CHART_MARGIN.RIGHT,
      top: CHART_MARGIN.TOP,
      bottom: CHART_MARGIN.BOTTOM,
    };

    const containerWidth = overtimeElement?.current?.clientWidth || 800;
    const containerHeight = overtimeElement?.current?.clientHeight || 600;

    return (
      <TooltipLineMarker
        year={year}
        x={x}
        yAxisMarkerExtent={{
          min: yAxisMarkerExtent.min - 2,
          max: yAxisMarkerExtent.max + 2,
        }}
        chartMargin={chartMargin}
        xScale={xScale}
        yScale={yScale}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
        overTimeLayout={yAxis}
        tooltipData={tooltipDisplay.contentData as { shareForYear?: number; exportValue?: number; importValue?: number; value?: number }}
        tradeDirection={tradeDirection as TradeDirection}
        tradeFlow={tradeFlow as TradeFlow}
      />
    );
  }, [
    tooltipDisplay,
    chartContainerOffset,
    yAxis,
    overtimeElement,
    tradeDirection,
    tradeFlow,
    isLoading,
  ]);

  return {
    tooltipContentElement,
    tooltipLineMarkerElement,
  };
};
